import { FolderIcon } from "@phosphor-icons/react";
import type { DragEvent, ReactNode } from "react";

interface FolderCardProps {
  name: string;
  /** Secondary line, e.g. "3 Designs" or "16 Items". */
  subtitle: ReactNode;
  onClick: () => void;
  /** Drag handlers from ``useSessionDropTarget``; omitted for non-targets. */
  dropProps?: {
    onDragOver: (event: DragEvent<HTMLButtonElement>) => void;
    onDragLeave: () => void;
    onDrop: (event: DragEvent<HTMLButtonElement>) => void | Promise<void>;
  };
  isOver?: boolean;
  isForbidden?: boolean;
}

/**
 * The folder card chrome, shared by the project folders and the asset-library
 * manufacturer/category folders so the two never drift apart visually.
 *
 * Deliberately presentational: it knows about a highlight state but nothing
 * about what is being dragged, which is why the project grid keeps owning
 * ``useSessionDropTarget`` and the asset grid can simply not pass one.
 */
export function FolderCard({
  name,
  subtitle,
  onClick,
  dropProps,
  isOver = false,
  isForbidden = false,
}: FolderCardProps) {
  return (
    <button
      type="button"
      onClick={onClick}
      {...dropProps}
      className={`flex min-w-0 w-full items-start gap-4 rounded-lg p-4 text-left transition hover:-translate-y-0.5 border border-[var(--stroke-field)] ${
        isOver
          ? "bg-[var(--bg-field)] ring-2 ring-[var(--accent-primary)]"
          : isForbidden
            ? "bg-red-900/30 ring-2 ring-red-500/40"
            : "bg-[var(--bg-default)] hover:bg-[#3f4b58]"
      }`}
    >
      <FolderIcon
        size={16}
        weight="fill"
        aria-hidden
        className="shrink-0 text-[var(--text-primary)]"
      />
      <div className="min-w-0 flex-1 text-left">
        <p className="truncate text-[15px] font-semibold leading-5 text-[var(--text-primary)]">
          {name}
        </p>
        <p className="text-[12px] font-medium leading-5 text-white/50">
          {subtitle}
        </p>
      </div>
    </button>
  );
}
